"use client";

import { faPlugCircleMinus, faPlugCirclePlus, faPlugCircleXmark, faRightFromBracket, faShuffle, faShareNodes } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { AnimatePresence, motion } from "framer-motion";
import { useContext, useEffect, useState } from "react";
import { AuthContext } from "@/components/auth/Auth";
import { useSeizeConnectContext } from "@/components/auth/SeizeConnectContext";
import { useChainSwitcher } from "@/components/header/useChainSwitcher";
import type { ApiIdentity } from "@/generated/models/ApiIdentity";
import type { ApiProfileProxy } from "@/generated/models/ApiProfileProxy";
import HeaderUserConnectedAccounts from "./connected/HeaderUserConnectedAccounts";
import HeaderUserProxyDropdownItem from "./HeaderUserProxyDropdownItem";
export default function HeaderUserMenuDropdown({
  isOpen,
  profile,
  onClose,
  onOpenShare
}: {
  readonly isOpen: boolean;
  readonly profile: ApiIdentity;
  readonly onClose: () => void;
  readonly onOpenShare?: (() => void) | undefined;
}) {
  const {
    address,
    isConnected,
    connectedAccounts,
    connectedAccountUnreadNotifications,
    canAddConnectedAccount,
    seizeConnect,
    seizeAddConnectedAccount,
    seizeDisconnect,
    seizeDisconnectAndLogout,
    seizeDisconnectAndLogoutAll,
    seizeSwitchConnectedAccount
  } = useSeizeConnectContext();
  const availableConnectedAccounts = connectedAccounts ?? [];
  const {
    activeProfileProxy,
    setActiveProfileProxy,
    receivedProfileProxies,
    setToast
  } = useContext(AuthContext);
  const hasProxySection = !!activeProfileProxy || receivedProfileProxies.length > 0;
  const {
    chains,
    currentChainName,
    nextChainName,
    switchToNextChain
  } = useChainSwitcher();
  const onActivateProfileProxy = async (profileProxy: ApiProfileProxy | null) => {
    await setActiveProfileProxy(profileProxy);
    onClose();
  };
  const getLabel = (): string => {
    if (profile.handle) {
      return profile.handle;
    }
    const wallet = profile?.wallets?.find(w => w.wallet.toLowerCase() === address?.toLocaleLowerCase());
    if (wallet?.display) {
      return wallet.display;
    }
    if (address) {
      return address.slice(0, 6);
    }
    throw new Error("No label found");
  };
  const [label, setLabel] = useState(getLabel());
  const [pendingAction, setPendingAction] = useState<string | null>(null);
  useEffect(() => setLabel(getLabel()), [profile, address]);
  const runMenuAction = async ({
    action,
    pendingKey,
    errorMessage
  }: {
    readonly action: () => void | Promise<void>;
    readonly pendingKey: string;
    readonly errorMessage: string;
  }) => {
    if (pendingAction) {
      return;
    }
    setPendingAction(pendingKey);
    try {
      await Promise.resolve(action());
      onClose();
    } catch (error) {
      console.error(errorMessage, error);
      setToast({
        message: errorMessage,
        type: "error"
      });
    } finally {
      setPendingAction(null);
    }
  };
  const onSelectConnectedAccount = (nextAddress: string) => {
    if (nextAddress.toLowerCase() === address?.toLowerCase()) {
      onClose();
      return;
    }
    void runMenuAction({
      action: () => seizeSwitchConnectedAccount(nextAddress),
      pendingKey: `switch-connected-account-${nextAddress.toLowerCase()}`,
      errorMessage: "Failed to switch connected account. Please try again."
    });
  };
  const onSwitchChain = () => {
    if (switchToNextChain()) {
      onClose();
    }
  };
  return <div>
      <AnimatePresence mode="wait" initial={false}>
        {isOpen && <motion.div className="tw-fixed tw-bottom-16 tw-left-6 tw-z-[9999] tw-mb-2 tw-mt-1 tw-w-72 tw-rounded-lg tw-bg-iron-800 tw-shadow-xl tw-ring-1 tw-ring-black tw-ring-opacity-5" initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: 20
      }} transition={{
        duration: 0.2
      }}>
            <div className="tw-mt-1 tw-w-full tw-overflow-hidden tw-rounded-md tw-bg-iron-800 tw-shadow-2xl">
              <div className="tw-flow-root tw-overflow-y-auto tw-overflow-x-hidden tw-py-2">
                <ul className="tw-m-0 tw-flex tw-list-none tw-flex-col tw-gap-y-2 tw-divide-x-0 tw-divide-y tw-divide-solid tw-divide-iron-700 tw-p-0">
                  {availableConnectedAccounts.length > 0 && <li className="tw-mx-0 tw-flex tw-flex-col tw-gap-y-2 tw-px-2">
                      <HeaderUserConnectedAccounts accounts={availableConnectedAccounts.map(account => ({
                  ...account,
                  unreadNotificationsCount: connectedAccountUnreadNotifications?.[account.address.toLowerCase()] ?? 0
                }))} onSelectAccount={onSelectConnectedAccount} canAddAccount={canAddConnectedAccount} onAddAccount={() => {
                  void runMenuAction({
                    action: seizeAddConnectedAccount,
                    pendingKey: "add-account",
                    errorMessage: "Failed to add connected account. Please try again."
                  });
                }} />
                    </li>}
                  {hasProxySection && <li className="tw-mx-0 tw-flex tw-flex-col tw-gap-y-2 tw-px-2">
                      <p className="tw-m-0 tw-px-3 tw-pt-2 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-iron-500">
                        Proxy Profile
                      </p>
                      <div className="tw-h-full">
                        <button type="button" className={`${activeProfileProxy ? "tw-bg-transparent hover:tw-bg-iron-700" : "tw-bg-iron-700"} tw-group tw-relative tw-flex tw-h-full tw-w-full tw-cursor-pointer tw-select-none tw-items-center tw-gap-x-3 tw-rounded-lg tw-border-none tw-px-3 tw-py-2.5 tw-text-left tw-text-white tw-transition tw-duration-300 tw-ease-out focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-primary-400`} onClick={() => onActivateProfileProxy(null)}>
                          {profile.pfp ? <div className="tw-relative tw-h-6 tw-w-6 tw-flex-none tw-flex-shrink-0 tw-overflow-hidden tw-rounded-lg" title={activeProfileProxy ? "Use primary profile" : "Primary profile"}>
                              <img src={profile.pfp} alt={`${label} avatar`} className="tw-absolute tw-inset-0 tw-block tw-h-full tw-w-full tw-rounded-lg tw-bg-iron-700 tw-object-cover tw-transition tw-duration-300 tw-ease-out" />
                            </div> : <div className={`${activeProfileProxy ? "tw-bg-iron-700 group-hover:tw-bg-iron-600" : "tw-bg-iron-600"} tw-h-6 tw-w-6 tw-flex-none tw-flex-shrink-0 tw-rounded-lg tw-transition tw-duration-300 tw-ease-out`} title={activeProfileProxy ? "Use primary profile" : "Primary profile"}></div>}
                          <div className="tw-inline-flex tw-w-full tw-items-center tw-justify-between tw-truncate">
                            <span className="tw-text-md tw-font-medium tw-text-white">
                              {label}
                            </span>
                            {!activeProfileProxy && <svg className="tw-ml-2 tw-h-5 tw-w-5 tw-flex-shrink-0 tw-text-primary-400 tw-transition tw-duration-300 tw-ease-out" viewBox="0 0 24 24" fill="none" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
                                <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>}
                          </div>
                        </button>
                      </div>
                      {receivedProfileProxies.map(profileProxy_0 => <HeaderUserProxyDropdownItem key={profileProxy_0.id} profileProxy={profileProxy_0} activeProfileProxy={activeProfileProxy} onActivateProfileProxy={onActivateProfileProxy} />)}
                    </li>}
                  <li className="tw-h-full tw-px-2 tw-pt-2">
                    {isConnected ? <button onClick={() => {
                  void runMenuAction({
                    action: seizeDisconnect,
                    pendingKey: "disconnect",
                    errorMessage: "Failed to disconnect wallet. Please try again."
                  });
                }} disabled={pendingAction !== null} type="button" aria-label="Disconnect" title="Disconnect" className="tw-relative tw-flex tw-h-full tw-w-full tw-cursor-pointer tw-select-none tw-items-center tw-gap-x-3 tw-rounded-lg tw-border-none tw-bg-transparent tw-px-3 tw-py-2.5 tw-text-left tw-text-md tw-font-medium tw-text-iron-300 tw-transition tw-duration-300 tw-ease-out hover:tw-bg-iron-700 hover:tw-text-iron-50 focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-primary-400">
                        <FontAwesomeIcon icon={faPlugCircleMinus} height={20} width={20} />
                        <span>Disconnect Wallet</span>
                      </button> : <button onClick={() => {
                  void runMenuAction({
                    action: seizeConnect,
                    pendingKey: "connect",
                    errorMessage: "Failed to open wallet connection. Please try again."
                  });
                }} disabled={pendingAction !== null} type="button" aria-label="Connect" title="Connect" className="tw-relative tw-flex tw-h-full tw-w-full tw-cursor-pointer tw-select-none tw-items-center tw-gap-x-3 tw-rounded-lg tw-border-none tw-bg-transparent tw-px-3 tw-py-2.5 tw-text-left tw-text-md tw-font-medium tw-text-iron-300 tw-transition tw-duration-300 tw-ease-out hover:tw-bg-iron-700 hover:tw-text-iron-50 focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-primary-400">
                        <FontAwesomeIcon icon={faPlugCirclePlus} height={20} width={20} />
                        <span>Connect Wallet</span>
                      </button>}
                  </li>
                  {isConnected && chains.length > 1 && <li className="tw-h-full tw-px-2 tw-pt-2">
                      <span className="tw-relative tw-flex tw-h-full tw-w-full tw-select-none tw-px-3 tw-pt-2.5 tw-text-left tw-text-md tw-font-medium tw-text-iron-300 tw-transition tw-duration-300 tw-ease-out">
                        Network: {currentChainName}
                      </span>
                      <button onClick={onSwitchChain} type="button" aria-label="Switch Chain" title="Switch Chain" className="tw-relative tw-flex tw-h-full tw-w-full tw-cursor-pointer tw-select-none tw-items-center tw-gap-x-3 tw-rounded-lg tw-border-none tw-bg-transparent tw-px-3 tw-py-2.5 tw-text-left tw-text-md tw-font-medium tw-text-iron-300 tw-transition tw-duration-300 tw-ease-out hover:tw-bg-iron-700 hover:tw-text-iron-50 focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-primary-400">
                        <FontAwesomeIcon icon={faShuffle} height={16} width={16} />
                        <span>Switch to {nextChainName}</span>
                      </button>
                    </li>}
                  {onOpenShare && <li className="tw-h-full tw-px-2 tw-pt-2">
                      <button onClick={onOpenShare} type="button" aria-label="Share" title="Share" className="tw-relative tw-flex tw-h-full tw-w-full tw-cursor-pointer tw-select-none tw-items-center tw-gap-x-3 tw-rounded-lg tw-border-none tw-bg-transparent tw-px-3 tw-py-2.5 tw-text-left tw-text-md tw-font-medium tw-text-iron-300 tw-transition tw-duration-300 tw-ease-out hover:tw-bg-iron-700 hover:tw-text-iron-50 focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-primary-400">
                        <FontAwesomeIcon icon={faShareNodes} height={20} width={20} />
                        <span>Share</span>
                      </button>
                    </li>}
                  <li className="tw-h-full tw-px-2 tw-pt-2">
                    <button onClick={() => {
                  void runMenuAction({
                    action: seizeDisconnectAndLogout,
                    pendingKey: "logout",
                    errorMessage: "Failed to sign out. Please try again."
                  });
                }} disabled={pendingAction !== null} type="button" aria-label="Disconnect & Logout" title="Disconnect & Logout" className="tw-relative tw-flex tw-h-full tw-w-full tw-cursor-pointer tw-select-none tw-items-center tw-gap-x-3 tw-rounded-lg tw-border-none tw-bg-transparent tw-px-3 tw-py-2.5 tw-text-left tw-text-md tw-font-medium tw-text-iron-300 tw-transition tw-duration-300 tw-ease-out hover:tw-bg-iron-700 hover:tw-text-iron-50 focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-primary-400">
                      <FontAwesomeIcon icon={faRightFromBracket} height={20} width={20} />
                      <span>{isConnected && `Disconnect & `}Logout</span>
                    </button>
                    {availableConnectedAccounts.length > 1 && <button onClick={() => {
                  void runMenuAction({
                    action: seizeDisconnectAndLogoutAll,
                    pendingKey: "logout-all",
                    errorMessage: "Failed to sign out all profiles. Please try again."
                  });
                }} disabled={pendingAction !== null} type="button" aria-label="Sign Out All Profiles" title="Sign Out All Profiles" className="tw-relative tw-flex tw-h-full tw-w-full tw-cursor-pointer tw-select-none tw-items-center tw-gap-x-3 tw-rounded-lg tw-border-none tw-bg-transparent tw-px-3 tw-py-2.5 tw-text-left tw-text-md tw-font-medium tw-text-iron-300 tw-transition tw-duration-300 tw-ease-out hover:tw-bg-iron-700 hover:tw-text-iron-50 focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-primary-400">
                        <FontAwesomeIcon icon={faPlugCircleXmark} height={20} width={20} />
                        <span>Sign Out All Profiles</span>
                      </button>}
                  </li>
                </ul>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}